import { useNavigate } from 'react-router-dom';
import React, { FC, useMemo } from 'react';
import { EndUserTypography } from '../../../../components/EndUserTypography';
import { WishlistPageT } from '../../../../lib/types/sections';
import { Category, DemandArea } from '../../../../lib/types/endUser';

import styles from './index.module.scss';
import { sendDataToAnalytics } from '../../../../lib/utils/sendDataToAnalytics';
import { HouseMobile } from '../../../../components/HouseMobile';
import { useAppDispatch, useAppSelector } from '../../../../lib/store/hooks';
import { sortCategoriesByPriority } from '../../../../lib/utils/helpers';
import { checkBoughtProductsInCategory } from '../../../../lib/utils/end-user-helpers';
import { setCurrentCategory } from '../../../../lib/store/slices/endUserSlice';
import { NewHouseModelMobile } from '../../../endUserHouseModelNew/components/NewHouseModelMobile';
import { END_USER_FRENCH_TENANT_MODEL_NAME } from '../../../../lib/utils/constants';
import { ROUTER_PATHS } from '../../../../lib/utils/router-paths';
import { FoundElementT } from '../../../../lib/hooks/endUserHooks/useGetCampaignObjectPositions';
import { CampaignObjectImage } from '../../../../components/CampaignObjectImage';
import { useIsEviTenant } from '../../../../lib/hooks/endUserHooks/useIsEviTenant';
import { EviNewHouseModelMobile } from '../../../../components/EviNewHouseModel/components/EviNewHouseModelMobile';

type Props = {
  content?: WishlistPageT;
  demandAreas?: DemandArea[];
  headlineCampaignObject?: FoundElementT;
};

export const EndUserWishlistHouseModelMobile: FC<Props> = ({
  demandAreas,
  content,
  headlineCampaignObject,
}) => {
  const { brandKit, tenantAndProject, demandArea } = useAppSelector(
    ({ endUser }) => endUser,
  );
  const dispatch = useAppDispatch();
  const isFrenchTenant = demandArea?.project?.name === END_USER_FRENCH_TENANT_MODEL_NAME;
  const isEviTenant = useIsEviTenant();

  const navigate = useNavigate();
  const handleAreaClick = (areaId: number, name: string) => {
    sendDataToAnalytics(
      'Wishlist house model',
      `Open demand area`,
      `Demand Area Id: ${areaId}, Demand Area name: ${name}`,
    );

    const demandArea = demandAreas?.find((area) => area.id === areaId);

    const sortedCategories = sortCategoriesByPriority(demandArea?.categories || []);

    let category: Category | undefined = sortedCategories[0];

    //hardcode below
    if (tenantAndProject?.subDomain === 'energie-schwaben') {
      if (demandArea?.name === 'Energie') {
        const gasCategory = sortedCategories.find((category) => category.name === 'Gas');
        const stromCategory = sortedCategories.find(
          (category) => category.name === 'Strom',
        );

        const isGasCovered = gasCategory && checkBoughtProductsInCategory(gasCategory);
        const isStromCovered =
          stromCategory && checkBoughtProductsInCategory(stromCategory);

        if (isGasCovered && !isStromCovered) {
          category = stromCategory;
        } else if (isStromCovered && !isGasCovered) {
          category = gasCategory;
        } else if (isGasCovered && isStromCovered) {
          category = gasCategory;
        } else {
          category = gasCategory;
        }
      }
    }

    setTimeout(() => {
      dispatch(setCurrentCategory({ category }));
    }, 100);

    const path = isFrenchTenant ? ROUTER_PATHS.houseModelFr : ROUTER_PATHS.houseModel;

    navigate(`/${path}/${areaId}`);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    areaId: number,
    name: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleAreaClick(areaId, name);
    }
  };

  const isPreprodEnergieSwaben =
    tenantAndProject?.domain === 'preprod.hausversorger.de' &&
    tenantAndProject?.tenantId === 11005;

  const applyNewHouse =
    (tenantAndProject?.subDomain === 'energie-schwaben' &&
      tenantAndProject?.tenantId === 11002) ||
    isPreprodEnergieSwaben;

  const RenderHouse = useMemo(() => {
    if (applyNewHouse) {
      return (
        <NewHouseModelMobile
          demandAreas={demandAreas}
          handleAreaClick={handleAreaClick}
          handleKeyDown={handleKeyDown}
        />
      );
    } else if (isEviTenant) {
      return (
        <EviNewHouseModelMobile
          demandAreas={demandAreas}
          handleAreaClick={handleAreaClick}
          handleKeyDown={handleKeyDown}
        />
      );
    } else {
      return (
        <HouseMobile
          demandAreas={demandAreas}
          handleAreaClick={handleAreaClick}
          handleKeyDown={handleKeyDown}
        />
      );
    }
  }, [applyNewHouse, demandAreas, handleAreaClick, handleKeyDown, isEviTenant]);

  return (
    <div
      className={styles.wrapper}
      style={{
        backgroundColor: brandKit?.colors.backgroundColor,
      }}
    >
      <div className={styles.headlineBlock}>
        <EndUserTypography
          type="headlineSecond"
          component="Title"
          style={{ marginBottom: '8px' }}
        >
          {content?.headline1}
        </EndUserTypography>
        <EndUserTypography
          type="headlineSixth"
          component="Title"
          style={{ marginBottom: 0 }}
        >
          {content?.subline}

          {headlineCampaignObject && (
            <CampaignObjectImage
              containerClassName={styles.campaignObjectContainer}
              campaignObject={headlineCampaignObject}
              imageClassName={styles.campaignObjectImage}
              completedIconClassName={styles.campaignCompletedIconContainer}
            />
          )}
        </EndUserTypography>
      </div>

      {RenderHouse}
    </div>
  );
};
